<template>
  <div class="timeline-player">
    <div class="player-container">
      <video 
        ref="videoPlayer"
        :src="videoUrl"
        class="main-video"
        playsinline
        controls
        @timeupdate="$emit('timeupdate', $event.target.currentTime)"
        @seeking="$emit('timeupdate', $event.target.currentTime)"
        @loadedmetadata="$emit('duration', $event.target.duration)"
        @seeked="$emit('seeked')"
        @play="$emit('play')"
        @pause="$emit('pause')"
      ></video>
      
      <!-- Pose Preview Overlay/Side-by-side -->
      <div v-show="targetVisible" class="pose-overlay">
        <canvas ref="targetCanvas" class="target-canvas"></canvas>
        <div class="overlay-label">当前参考帧姿态</div>
        <div class="overlay-close" @click="$emit('clear-target')">
          <v-icon size="14">mdi-close</v-icon>
        </div>
      </div>

      <!-- Main Player Actions -->
      <div class="player-actions">
        <!-- 下载单帧 -->
        <v-btn
          icon="mdi-camera"
          variant="flat"
          size="small"
          class="capture-btn"
          @click="$emit('download-image')"
          title="下载当前画面单帧"
        ></v-btn>

        <!-- 下载短片段 -->
        <v-menu location="top center" offset="10">
          <template v-slot:activator="{ props }">
            <v-btn
              icon="mdi-content-cut"
              variant="flat"
              size="small"
              class="capture-btn"
              v-bind="props"
              title="添加到剪辑池"
            ></v-btn>
          </template>
          <v-list density="compact" bg-color="#333" theme="dark" elevation="12" min-width="160">
            <v-list-subheader class="text-white px-4">添加到当前剪辑池</v-list-subheader>
            <v-list-item 
              v-for="duration in [1, 2, 4, 6]" 
              :key="duration"
              @click="$emit('add-to-pool', duration)"
              class="cursor-pointer"
            >
              <template v-slot:prepend>
                <v-icon size="small" color="#00d2d3">mdi-clock-outline</v-icon>
              </template>
              <v-list-item-title>{{ duration }}秒 (±{{ duration/2 }}s)</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item @click="onCustomClipClick" class="cursor-pointer">
              <template v-slot:prepend>
                <v-icon size="small" color="#00d2d3">mdi-pencil-outline</v-icon>
              </template>
              <v-list-item-title>自定义长度...</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Hidden video for frame capture -->
    <video 
      ref="hiddenVideo" 
      :src="videoUrl" 
      style="display: none;" 
      @seeked="$emit('hidden-seeked')"
    ></video>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

defineProps({
  videoUrl: String,
  targetVisible: Boolean,
  isPlaying: Boolean
})

const emit = defineEmits(['timeupdate', 'duration', 'seeked', 'hidden-seeked', 'clear-target', 'play', 'pause', 'toggle-play', 'download-image', 'add-to-pool'])

const videoPlayer = ref(null)
const hiddenVideo = ref(null)
const targetCanvas = ref(null)

const onCustomClipClick = () => {
  const val = window.prompt('请输入下载片段的总长度（秒）：', '2')
  if (val && !isNaN(parseFloat(val))) {
    emit('add-to-pool', parseFloat(val))
  }
}

defineExpose({
  videoPlayer,
  hiddenVideo,
  targetCanvas
})
</script>

<style scoped>
.timeline-player {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #000;
  position: relative;
}

.player-container {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.main-video {
  max-width: 100%;
  max-height: 100%;
}

.pose-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 140px;
  aspect-ratio: 9/16;
  background: rgba(0,0,0,0.8);
  border: 2px solid #00d2d3;
  border-radius: 4px;
  overflow: hidden;
}

.target-canvas {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.player-actions {
  position: absolute;
  bottom: 60px;
  right: 15px;
  z-index: 5;
  display: flex;
  gap: 12px;
  align-items: center;
}

.capture-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: #00d2d3 !important;
}

.capture-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  color: #ffffff !important;
}

.overlay-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 10px;
  color: #fff;
  background: rgba(0,0,0,0.5);
  text-align: center;
  padding: 2px 0;
}

.overlay-close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  background: rgba(0,0,0,0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
</style>
